import React, { FC, ReactElement, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { COMMAND_STATE } from "../../../constants/command-status";
import { TITLE_BKGCOLOR, TITLE_FGCOLOR } from "../../../constants/widget-constants";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { useDashboardContext } from "../../../contexts/dashboard/DashboardContext";
import { useLocale } from "../../../contexts/localeContext";
import { ExecuteCommand } from "../../../models/ExecuteCommand";
import { SettingData } from "../../../models/SettingData";
import { Widget } from "../../../models/Widget";
import WizardSideBarStep from "../../../models/WizardSideBarStep";
import { executeCommandWidgetCommands } from "../../../services/executeCommandService";
import { saveSettingData } from "../../../services/settingDataService";
import { getErrorMessage, showError } from "../../../services/utilities/ServiceError";
import Tooltip from "../../Tooltip/Tooltip";
import { useDialog } from "../../dialog/DialogContext";
import BaseWidget from "../base-widget";
import ColorStep from "../color-step";
import EditWidgetModal from "../edit-wiget-modal";
import CommandIcon from "./CommandIcon";
import { buildCmdFailureMsg, buildEmergencyCommand, buildLockdownModeCommand, buildOpenDoorCommand, buildResetAPBCommand, isResetAPBSupported } from "./commandUtil";
import SummaryStep from "./summary-step";


interface CommandWidgetProps {
    widget: Widget;
    previewMode?: boolean;
}

type CommandWidgetState = {
    emergencyLockdown: boolean;
    emergencyOpen: boolean;
    apbReset: boolean
}

const doesSupportResetApb = (relationships): boolean => {
    return relationships?.length > 0 && relationships.some((data) => 'zoneId' in data || 'doorId' in data);
};

const CommandWidget: FC<CommandWidgetProps> = ({ widget, previewMode = false }) => {
    const { translate } = useLocale();
    const dialog = useDialog();
    const [status, setStatus] = useState<CommandWidgetState>({
        emergencyLockdown: false,
        emergencyOpen: false,
        apbReset: false
    });
    const [enableOpenDoor, setEnableOpenDoor] = useState(false);
    const [open, setOpen] = useState(false);
    const [step, setStep] = useState(0);
    const timeoutIdRefAPB = useRef<NodeJS.Timeout | null>(null);
    const timeoutIdRefOpenDoor = useRef<NodeJS.Timeout | null>(null);
    const dashboard = useDashboardContext();
    const [isApbSupported] = useState<boolean>(doesSupportResetApb(widget?.relationships));

    const steps: WizardSideBarStep[] = [
        {
            title: translate.byKey(TranslationKeys.widgetConfig),
            description: '',
            substeps: []
        },
        {
            title: translate.byKey(TranslationKeys.summary),
            description: '',
            substeps: []
        },
    ];

    const onNext = () => {
        setStep(step + 1);
    }

    const onBack = () => {
        setStep(step - 1);
    }

    const content: ReactElement[] = [
        <ColorStep onNext={onNext} />,
        <SummaryStep onBack={onBack} />
    ];

    const showFailureMsg = (cmdSubType: string, response: any, isPartialSuccess: boolean) => {
        let countFailures = 0;
        let listFailures: any = [];

        // Has details of which failed.
        if (response.message && response.message.errors && response.message.errors.length > 0) {
            response.message.errors.forEach((error: any) => {
                countFailures++;

                // Only show the first 3 failures. There might be too many to show.
                if (countFailures < 4) {
                    listFailures.push(error.detail);
                }
            });

            const msg = buildCmdFailureMsg(cmdSubType, translate, countFailures, listFailures);
            const msgTitle = isPartialSuccess ? translate.byKey(TranslationKeys.partialSuccess) : translate.byKey(TranslationKeys.error);
            dialog.pushError({ title: msgTitle, message: msg });
        }
        // No details, just show partial success message.
        else {
            toast.success(translate.byKey(TranslationKeys.partialSuccess));
        }
    }

    const executeCommand = (command: ExecuteCommand, statusData?: CommandWidgetState) => {
        try {
            executeCommandWidgetCommands(command)
                .then((response: any) => {

                    // If the command was successful, including partial success, update the status.
                    if (response.status === COMMAND_STATE.COMMAND_COMPLETE
                        || response.status == COMMAND_STATE.COMMAND_PARTIAL_SUCCESS
                    ) {
                        if (statusData) {
                            setStatus(statusData);
                        }

                        return (response.status === COMMAND_STATE.COMMAND_COMPLETE)
                            ? toast.success(translate.byKey(TranslationKeys.commandExecuted))
                            : showFailureMsg(command.cmdSubType, response, true);
                    }
                    // If the command failed, don't update state but show the failure message.
                    else {
                        showFailureMsg(command.cmdSubType, response, false);
                    }
                })
                .catch((error) => {
                    dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
                })
        } catch (error) {
            showError(error, toast.warn);
        }
    }

    const executeResetAPBCommand = () => {
        if (isResetAPBSupported(widget)) {
            let statusData = { ...status, apbReset: !status.apbReset };
            executeCommand(buildResetAPBCommand(widget), statusData);

            timeoutIdRefAPB.current = setTimeout(() => {
                setStatus((prevState) => ({ ...prevState, apbReset: false }));
            }, 3000);
        }
        else {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: translate.byKey(TranslationKeys.resetAPBOnlySupportedOnSitesAndControllers) });
        }
    }

    const onEdit = (key: string) => {
        dashboard.setIsDraggable(false);
        setOpen(true);
    }

    const onClose = () => {
        setStep(0);
        setOpen(false);
        dashboard.setIsDraggable(true);
    }

    const getTitle = () => {
        return widget.relationships.length > 0 && widget.relationships[0]["name"];
    }

    const onSave = (values: Widget) => {
        const titleForegroundColorId = values.settingDatasRefs?.find((item: any) => item.eSysname === TITLE_FGCOLOR)?.id;
        const titleBackgroundColorId = values.settingDatasRefs?.find((item: any) => item.eSysname === TITLE_BKGCOLOR)?.id;

        const settingDataToEdit: SettingData[] = [
            {
                sdValstring: values.titleForegroundColor,
                id: titleForegroundColorId
            },
            {
                sdValstring: values.titleBackgroundColor,
                id: titleBackgroundColorId
            }
        ];

        Promise.all(settingDataToEdit.map((settingData) => {
            return saveSettingData(settingData);
        })).then(() => {
            dashboard.onEditReload(values);
        }).catch((error) => {
            showError(error, toast.warn);
        }).finally(() => {
            onClose();
        });
    }

    useEffect(() => {
        return () => { // cleanup timers to avoid memory leaks
            if (timeoutIdRefAPB.current) {
                clearTimeout(timeoutIdRefAPB.current);
            }
            if (timeoutIdRefOpenDoor.current) {
                clearTimeout(timeoutIdRefOpenDoor.current);
            }
        }
    }, [])

    return <div style={{ width: "205px", height: "234px" }} >
        <BaseWidget
            titleBackgroundColor={widget.titleBackgroundColor}
            titleForegroundColor={widget.titleForegroundColor}
            previewMode={previewMode}
            onEdit={onEdit}
            widgetKey={widget.id.toString()}
            title={getTitle()}>
            <div className="h-full">
                <div className="flex flex-row justify-evenly">
                    <button
                        className="w-32 overflow-hidden flex flex-col items-center justify-center cursor-pointer"
                        disabled={previewMode}
                        id="emergencyModeButton"
                        onClick={() => {
                            if (timeoutIdRefAPB.current) {
                                clearTimeout(timeoutIdRefAPB.current);
                            }
                            setEnableOpenDoor(false)
                            const statusData = {
                                ...status,
                                emergencyOpen: !status.emergencyOpen,
                                emergencyLockdown: false,
                                openDoor: false
                            };
                            executeCommand(buildEmergencyCommand(!status.emergencyOpen, widget), statusData);
                        }}>
                        <div className="m-2">
                            <CommandIcon icon={"EM_OPEN"} enabled={status.emergencyOpen} />
                        </div>
                        <Tooltip text={translate.byKey(TranslationKeys.emergencyOpen)} direction="top" show={true} width={'w-full'}>
                            <p className="text-xs font-bold mb-2 truncate max-w-full px-3">{translate.byKey(TranslationKeys.emergencyOpen)}</p>
                        </Tooltip>
                    </button>
                    <div className="border-r-2" />
                    <button
                        className="w-32 flex flex-col items-center justify-center cursor-pointer overflow-hidden"
                        disabled={previewMode}
                        id="emergencyLockdownButton"
                        onClick={() => {
                            if (timeoutIdRefAPB.current) {
                                clearTimeout(timeoutIdRefAPB.current);
                            }
                            setEnableOpenDoor(false)
                            const statusData = {
                                ...status,
                                emergencyLockdown: !status.emergencyLockdown,
                                emergencyOpen: false,
                                openDoor: false
                            };
                            executeCommand(buildLockdownModeCommand(!status.emergencyLockdown, widget), statusData);
                        }}>
                        <div className="m-2">
                            <CommandIcon icon={"EM_LOCK"} enabled={status.emergencyLockdown} />
                        </div>
                        <Tooltip text={translate.byKey(TranslationKeys.emergencyLockdown)} direction="top" show={true} width={'w-full'}>
                            <p className="text-xs font-bold mb-2 truncate max-w-full px-3">{translate.byKey(TranslationKeys.emergencyLockdown)}</p>
                        </Tooltip>
                    </button>
                </div>
                <div className="flex flex-row justify-evenly border-t-2">
                    <button
                        className="w-32 flex flex-col items-center justify-center cursor-pointer overflow-hidden"
                        disabled={previewMode}
                        id="openDoorButton"
                        onClick={() => {
                            executeCommand(buildOpenDoorCommand(widget))
                            setEnableOpenDoor(true)
                        }}
                        onMouseUp={() => {
                            timeoutIdRefOpenDoor.current = setTimeout(() => {
                                setEnableOpenDoor(false)
                            }, 5000)
                        }}>
                        <div className="m-2">
                            <CommandIcon icon={"OPEN_DOOR"} enabled={enableOpenDoor} />
                        </div>
                        <Tooltip text={translate.byKey(TranslationKeys.openDoor)} direction="top" show={true}>
                            <p className="text-xs font-bold mb-2 truncate max-w-full px-3">{translate.byKey(TranslationKeys.openDoor)}</p>
                        </Tooltip>
                    </button>
                    <div className="border-r-2" />
                    <button
                        className={`w-32 flex flex-col items-center justify-center overflow-hidden ${isApbSupported ? 'cursor-default' : 'cursor-pointer'}`}
                        disabled={previewMode || isApbSupported}
                        id="resetAPBButton"
                        onClick={() => executeResetAPBCommand()}>
                        {!isApbSupported && <>
                            <div className="m-2">
                                <CommandIcon icon={"APB_REST"} enabled={status.apbReset} />
                            </div>
                            <Tooltip text={translate.byKey(TranslationKeys.apbReset)} direction="top" show={true} width={'w-full'}>
                                <p className={`text-xs font-bold mb-1 truncate max-w-full px-3`}>{translate.byKey(TranslationKeys.apbReset)}</p>
                            </Tooltip>
                        </>}
                    </button>
                </div>
            </div>
        </BaseWidget>
        <EditWidgetModal
            isOpen={open}
            onClose={onClose}
            steps={steps}
            content={content}
            step={step}
            title={translate.byKey(TranslationKeys.commands)}
            widget={widget}
            onSave={onSave} />
    </div>
}

export default CommandWidget;
